import React, { useState, useEffect } from 'react';
import { recurringTaskAPI, clientAPI, serviceAPI, taskTypeAPI, userAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import {
  Plus,
  RefreshCw,
  Pause,
  Play,
  Edit2,
  Trash2,
  Search,
  Calendar,
  Building2,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
  StopCircle,
} from 'lucide-react';
import { formatDate } from '../utils/dateFormat';

const RecurringTasks = () => {
  const { isPartner, isManager } = useAuth();
  const [recurringTasks, setRecurringTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClient, setFilterClient] = useState('');
  
  // Form
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    client_id: '',
    service_id: '',
    task_type_id: '',
    recurrence_type: 'monthly',
    day_of_month: 1,
    start_date: '',
    assigned_to: '',
    notes: '',
  });
  
  // Client search
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [recurringRes, clientsRes, servicesRes, taskTypesRes, usersRes] = await Promise.all([
        recurringTaskAPI.getAll(),
        clientAPI.getAll(),
        serviceAPI.getAll(),
        taskTypeAPI.getAll(),
        userAPI.getAll(),
      ]);
      setRecurringTasks(recurringRes.data);
      setClients(clientsRes.data);
      setServices(servicesRes.data);
      setTaskTypes(taskTypesRes.data);
      setUsers(usersRes.data.filter(u => u.is_active));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.client_id || !formData.service_id || 
        !formData.task_type_id || !formData.recurrence_type || !formData.start_date) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      if (editingTask) {
        await recurringTaskAPI.update(editingTask.id, formData);
      } else {
        await recurringTaskAPI.create(formData);
      }
      resetForm();
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save recurring task');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingTask(null);
    setFormData({
      title: '',
      client_id: '',
      service_id: '',
      task_type_id: '',
      recurrence_type: 'monthly',
      day_of_month: 1,
      start_date: '',
      assigned_to: '',
      notes: '',
    });
    setClientSearchTerm('');
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      client_id: task.client_id,
      service_id: task.service_id,
      task_type_id: task.task_type_id,
      recurrence_type: task.recurrence_type,
      day_of_month: task.day_of_month || 1,
      start_date: task.start_date?.split('T')[0] || '',
      assigned_to: task.assigned_to || '',
      notes: task.notes || '',
    });
    setClientSearchTerm(task.client_name);
    setShowForm(true);
  };

  const handlePause = async (task) => {
    try {
      await recurringTaskAPI.pause(task.id);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to pause/resume recurring task');
    }
  };

  const handleForceStop = async (task) => {
    if (!window.confirm('Are you sure you want to FORCE STOP this recurring task? This action is permanent and cannot be undone.')) {
      return;
    }
    try {
      await recurringTaskAPI.stop(task.id);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to force stop recurring task');
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm('Are you sure you want to delete this recurring task?')) {
      return;
    }
    try {
      await recurringTaskAPI.remove(task.id);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete recurring task');
    }
  };

  const handleClientSelect = (client) => {
    setFormData({ ...formData, client_id: client.id });
    setClientSearchTerm(client.name);
    setShowClientDropdown(false);
  };

  // Filter clients based on search
  const filteredClients = clients.filter((client) => {
    if (!clientSearchTerm) return true;
    const search = clientSearchTerm.toLowerCase();
    return (
      client.name?.toLowerCase().includes(search) ||
      client.client_code?.toLowerCase().includes(search) ||
      client.phone?.includes(search)
    );
  });

  // Filter recurring tasks
  const filteredTasks = recurringTasks.filter((task) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        task.title?.toLowerCase().includes(search) ||
        task.client_name?.toLowerCase().includes(search) ||
        task.service_name?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    if (filterStatus && task.status !== filterStatus) return false;
    if (filterClient && task.client_id !== filterClient) return false;
    return true;
  });

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      paused: 'bg-yellow-100 text-yellow-700',
      stopped: 'bg-red-100 text-red-700',
    };
    return styles[status] || styles.active;
  };

  const getRecurrenceLabel = (type) => {
    const labels = {
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      'half-yearly': 'Half-Yearly',
      yearly: 'Yearly',
    };
    return labels[type] || type;
  };

  const canManage = isPartner || isManager;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold" data-testid="recurring-tasks-title">Recurring Tasks</h2>
          <p className="text-gray-600 text-sm">Tasks are automatically generated based on schedule</p>
        </div>
        <div className="flex space-x-2">
          {canManage && (
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
              data-testid="create-recurring-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Recurring Task
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">
              <Search className="w-4 h-4 inline mr-2" />
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by title, client, or service..."
              className="w-full px-3 py-2 border rounded-lg"
              data-testid="search-recurring-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="stopped">Stopped</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Client</label>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Clients</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-3 text-sm text-gray-600">
          Showing {filteredTasks.length} of {recurringTasks.length} recurring tasks
        </div>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold mb-4">
            {editingTask ? 'Edit Recurring Task' : 'Create Recurring Task'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Monthly GST Filing"
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                  data-testid="recurring-title-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Client *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={clientSearchTerm}
                    onChange={(e) => {
                      setClientSearchTerm(e.target.value);
                      setShowClientDropdown(true);
                    }}
                    onFocus={() => setShowClientDropdown(true)}
                    placeholder="Search client..."
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                    data-testid="recurring-client-search"
                  />
                  {showClientDropdown && filteredClients.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredClients.slice(0, 10).map((client) => (
                        <div
                          key={client.id}
                          onClick={() => handleClientSelect(client)}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          <div className="font-medium">{client.name}</div>
                          {client.phone && (
                            <div className="text-sm text-gray-500">{client.phone}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Service *</label>
                <select
                  value={formData.service_id}
                  onChange={(e) => setFormData({ ...formData, service_id: e.target.value, task_type_id: '' })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select Service</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>{service.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Task Type (Sub-Service) *</label>
                <select
                  value={formData.task_type_id}
                  onChange={(e) => setFormData({ ...formData, task_type_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                  disabled={!formData.service_id}
                >
                  <option value="">Select Task Type</option>
                  {taskTypes
                    .filter((tt) => tt.service_id === formData.service_id)
                    .map((taskType) => (
                      <option key={taskType.id} value={taskType.id}>{taskType.name}</option>
                    ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Recurrence *</label>
                <select
                  value={formData.recurrence_type}
                  onChange={(e) => setFormData({ ...formData, recurrence_type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                  data-testid="recurrence-type-select"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="half-yearly">Half-Yearly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              {['monthly', 'quarterly', 'half-yearly', 'yearly'].includes(formData.recurrence_type) && (
                <div>
                  <label className="block text-sm font-medium mb-1">Day of Month *</label>
                  <select
                    value={formData.day_of_month}
                    onChange={(e) => setFormData({ ...formData, day_of_month: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                    data-testid="day-of-month-select"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Task generates on this day each {formData.recurrence_type === 'monthly' ? 'month' : formData.recurrence_type === 'quarterly' ? 'quarter' : formData.recurrence_type === 'half-yearly' ? 'half-year' : 'year'}</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-1">Start Date *</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                  data-testid="recurring-start-date"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Assign To *</label>
                <select
                  value={formData.assigned_to}
                  onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                  data-testid="recurring-assigned-to"
                >
                  <option value="">Select User</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>{user.full_name} ({user.role})</option>
                  ))}
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="2"
                  placeholder="Optional notes for generated tasks..."
                />
              </div>
            </div>
            
            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={loading}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                data-testid="save-recurring-button"
              >
                {loading ? 'Saving...' : (editingTask ? 'Update' : 'Create')}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-full">
              <RefreshCw className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-xl font-bold">{recurringTasks.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-full">
              <Play className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-xl font-bold">{recurringTasks.filter(t => t.status === 'active').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="bg-yellow-100 p-3 rounded-full">
              <Pause className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Paused</p>
              <p className="text-xl font-bold">{recurringTasks.filter(t => t.status === 'paused').length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recurring Tasks List */}
      <div className="space-y-4">
        {loading && (
          <div className="text-center py-8">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
            <p className="text-gray-600 mt-2">Loading...</p>
          </div>
        )}
        
        {!loading && filteredTasks.length === 0 && (
          <div className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {searchTerm || filterStatus || filterClient
                ? 'No recurring tasks found matching your filters.'
                : 'No recurring tasks yet. Create one to automate task generation.'}
            </p>
          </div>
        )}
        
        {filteredTasks.map((task) => (
          <div key={task.id} className="bg-white p-6 rounded-lg shadow border" data-testid="recurring-task-card">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2 flex-wrap">
                  <h3 className="text-lg font-semibold">{task.title}</h3>
                  <span className={`px-2 py-1 text-xs rounded ${getStatusBadge(task.status)}`}>
                    {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                  </span>
                  <span className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-700">
                    {getRecurrenceLabel(task.recurrence_type)}
                  </span>
                  {task.day_of_month && ['monthly', 'quarterly', 'half-yearly', 'yearly'].includes(task.recurrence_type) && (
                    <span className="px-2 py-1 text-xs rounded bg-blue-50 text-blue-700">
                      Day {task.day_of_month}
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm text-gray-600 mb-3">
                  <div className="flex items-center">
                    <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                    {task.client_name}
                  </div>
                  <div className="flex items-center">
                    <FileText className="w-4 h-4 mr-2 text-gray-400" />
                    {task.service_name}
                  </div>
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-2 text-gray-400" />
                    {task.assigned_to_name}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                    Start: {formatDate(task.start_date)}
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-2 text-gray-400" />
                    Next: {task.next_run_date ? formatDate(task.next_run_date) : 'N/A'}
                  </div>
                </div>
                
                {task.notes && (
                  <p className="text-sm text-gray-500 italic">{task.notes}</p>
                )}
                
                <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                  <span>Generated: {task.generated_count || 0} tasks</span>
                  {task.last_generated && (
                    <span>Last: {formatDate(task.last_generated)}</span>
                  )}
                  <span>By: {task.created_by_name}</span>
                </div>
              </div>
              
              {/* Action Buttons */}
              {canManage && (
                <div className="flex flex-col space-y-2 ml-4">
                  {task.status !== 'stopped' && (
                    <>
                      <button
                        onClick={() => handlePause(task)}
                        className={`px-3 py-2 rounded text-sm flex items-center ${
                          task.status === 'paused'
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        }`}
                        title={task.status === 'paused' ? 'Resume' : 'Pause'}
                        data-testid="pause-resume-button"
                      >
                        {task.status === 'paused' ? (
                          <>
                            <Play className="w-4 h-4 mr-1" />
                            Resume
                          </>
                        ) : (
                          <>
                            <Pause className="w-4 h-4 mr-1" />
                            Pause
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(task)}
                        className="bg-blue-100 text-blue-700 px-3 py-2 rounded text-sm hover:bg-blue-200 flex items-center"
                        data-testid="edit-recurring-button"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </button>
                      {/* Force Stop - Partner Only */}
                      {isPartner && (
                        <button
                          onClick={() => handleForceStop(task)}
                          className="bg-red-600 text-white px-3 py-2 rounded text-sm hover:bg-red-700 flex items-center"
                          title="Force Stop (Permanent)"
                          data-testid="force-stop-button"
                        >
                          <StopCircle className="w-4 h-4 mr-1" />
                          Force Stop
                        </button>
                      )}
                    </>
                  )}
                  <button
                    onClick={() => handleDelete(task)}
                    className="bg-gray-100 text-gray-700 px-3 py-2 rounded text-sm hover:bg-gray-200 flex items-center"
                    title="Delete"
                    data-testid="delete-recurring-button"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h4 className="font-medium text-green-800 mb-2">Automatic Task Generation</h4>
        <ul className="text-sm text-green-700 space-y-1 list-disc list-inside">
          <li><strong>Daily:</strong> Task generated every day automatically</li>
          <li><strong>Weekly:</strong> Task generated every week automatically</li>
          <li><strong>Monthly:</strong> Task generated on Day 1 of each month (due at month end)</li>
          <li><strong>Quarterly:</strong> Task generated on Day 1 of quarter (Jan, Apr, Jul, Oct)</li>
          <li><strong>Half-Yearly:</strong> Task generated on Day 1 of half (Jan, Jul)</li>
          <li><strong>Yearly:</strong> Task generated on January 1st each year</li>
          <li>Tasks are <strong>automatically created</strong> - no manual action needed!</li>
          <li>Generated tasks are assigned to the selected user</li>
        </ul>
      </div>
    </div>
  );
};

export default RecurringTasks;
